import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface TeamMember {
  user_id: string;
  event_id: string;
  skills: string[];
  team_preferences?: string;
  availability_status: string;
  registered_at: string;
  updated_at: string;
  // Enhanced user details for internal tool
  email?: string;
  display_name?: string;
}

interface TeamDiscoveryProps {
  eventId: string;
}

const TeamDiscovery: React.FC<TeamDiscoveryProps> = ({ eventId }) => {
  const { token } = useAuth();
  const [availableMembers, setAvailableMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [skillFilter, setSkillFilter] = useState<string>('');

  useEffect(() => {
    const fetchAvailableMembers = async () => {
      if (!token || !eventId) return;

      setLoading(true);
      setError('');

      try {
        const members = await apiGet<TeamMember[]>(`/events/${eventId}/participants/unassigned`, token);
        setAvailableMembers(members);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableMembers();
  }, [token, eventId]);

  const filteredMembers = availableMembers.filter(member => {
    if (!skillFilter.trim()) return true;
    
    const filterSkills = skillFilter.toLowerCase().split(',').map(s => s.trim());
    return filterSkills.some(filterSkill => 
      member.skills.some(memberSkill => 
        memberSkill.toLowerCase().includes(filterSkill)
      )
    );
  });

  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <p>Loading available team members...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      marginTop: '20px'
    }}>
      <h3 style={{ marginTop: '0', marginBottom: '15px', color: '#333' }}>
        Discover Team Members
      </h3>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '15px',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="skill-filter" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Filter by Skills:
        </label>
        <input
          id="skill-filter"
          type="text"
          value={skillFilter}
          onChange={(e) => setSkillFilter(e.target.value)}
          placeholder="e.g., Python, React, AWS (comma-separated)"
          style={{ 
            width: '100%', 
            padding: '8px', 
            borderRadius: '4px', 
            border: '1px solid #ccc',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Search for participants with specific skills
        </small>
      </div>

      {filteredMembers.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          color: '#6c757d'
        }}>
          {availableMembers.length === 0 
            ? 'No participants have registered for this event yet.'
            : 'No participants match your skill filter.'
          }
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {filteredMembers.map((member: TeamMember) => (
            <div key={member.user_id} className="border p-4 rounded-lg">
              <h4 className="font-medium">
                {member.display_name || member.email || `ID: ${member.user_id.substring(0, 8)}...`}
              </h4>
              <p className="text-sm text-gray-600">Skills: {member.skills.join(', ')}</p>
              {member.team_preferences && (
                <p className="text-sm text-gray-600">Preferences: {member.team_preferences}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '10px', 
        backgroundColor: '#e7f3ff', 
        borderRadius: '4px',
        border: '1px solid #b3d7ff' 
      }}>
        <small style={{ color: '#0056b3', fontSize: '12px' }}>
          💡 Use this list to discover potential team members for your event. 
          Contact participants directly to form teams based on complementary skills and interests.
        </small>
      </div>
    </div>
  );
};

export default TeamDiscovery;
